import { API_URL, API_CONFIG, TIMEOUTS } from '../constants/config';
import { apiFetchWithTokenRefresh } from '../utils/tokenInterceptor';
import { isCookieAuthMode } from '../utils/authMode';

export interface RegisterRequest {
  email: string;
  password: string;
  name?: string;
  invite_code?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface ForgotPasswordRequest {
  email: string;
  /** @deprecated Backend ignores this - use request-password-reset + confirm-password-reset flow */
  new_password?: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data?: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    token_type: string;
  };
  user?: BackendUser;
  error?: string;
  detail?: string;
  /** When true, user must verify email before signing in (no tokens returned) */
  requires_email_verification?: boolean;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface BackendUser {
  id: string;
  email: string;
  name: string;
  supabase_uid: string;
  is_active: boolean;
  is_admin: boolean;
  role: string;
  subscription_plan: string;
  subscription_status: string;
  subscription_expires_at?: string;
  onboarding_completed: boolean;
  age_bracket?: string;
  industry?: string;
  is_student?: boolean;
  profile_image_url?: string;
  bio?: string;
  preferences?: Record<string, unknown>;
  color_generator_used_at?: string;
  virtual_drape_used_at?: string;
  color_analysis_used_at?: string;
  created_at: string;
  updated_at: string;
  is_premium: boolean;
  email_verified?: boolean;
}

const apiFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const isFormData = options.body instanceof FormData;

  let headers: HeadersInit = {};

  if (!isFormData) {
    headers = { ...API_CONFIG.headers };
  }

  if (options.headers) {
    headers = { ...headers, ...(options.headers as Record<string, string>) };
  }

  if (
    isFormData &&
    typeof headers === 'object' &&
    headers !== null &&
    'Content-Type' in headers
  ) {
    delete (headers as Record<string, string>)['Content-Type'];
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUTS.API_REQUEST);

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorMessage: string;

      try {
        const errorData = await response.json();

        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            const validationErrors = errorData.detail
              .map(
                (err: { loc?: string[]; msg?: string }) =>
                  `${err.loc?.[err.loc.length - 1] || 'field'}: ${err.msg}`
              )
              .join(', ');
            errorMessage = `Validation failed: ${validationErrors}`;
          } else {
            errorMessage = errorData.detail;
          }
        } else {
          errorMessage =
            errorData.message ||
            `Request failed with status ${response.status}`;
        }
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }

      throw new Error(errorMessage);
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again in a moment.');
    }
    throw error;
  }
};

export const authAPI = {
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (isCookieAuthMode()) headers['X-Request-Cookie-Auth'] = 'true';
    const response = await apiFetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      credentials: isCookieAuthMode() ? 'include' : 'same-origin',
    });
    return response.json();
  },

  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (isCookieAuthMode()) headers['X-Request-Cookie-Auth'] = 'true';
    const response = await apiFetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      credentials: isCookieAuthMode() ? 'include' : 'same-origin',
    });
    return response.json();
  },

  refreshToken: async (refreshToken?: string): Promise<AuthResponse> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (isCookieAuthMode()) headers['X-Request-Cookie-Auth'] = 'true';
    const response = await apiFetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers,
      body: refreshToken
        ? JSON.stringify({ refresh_token: refreshToken })
        : '{}',
      credentials: isCookieAuthMode() ? 'include' : 'same-origin',
    });

    return response.json();
  },

  logout: async (): Promise<void> => {
    if (isCookieAuthMode()) {
      await apiFetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    }
  },

  getCurrentUser: async (_token: string): Promise<BackendUser> => {
    const response = await apiFetchWithTokenRefresh(`${API_URL}/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.json();
  },

  healthCheck: async (): Promise<{
    status: string;
    environment: string;
    version: string;
  }> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      TIMEOUTS.API_REQUEST
    );

    const response = await fetch(`${API_URL.replace('/api/v1', '')}/health`, {
      method: 'GET',
      headers: API_CONFIG.headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }

    return response.json();
  },

  changePassword: async (
    data: ChangePasswordRequest,
    _token: string
  ): Promise<AuthResponse> => {
    const response = await apiFetchWithTokenRefresh(
      `${API_URL}/auth/change-password`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    return response.json();
  },

  forgotPassword: async (
    data: ForgotPasswordRequest
  ): Promise<AuthResponse> => {
    const response = await apiFetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    return response.json();
  },

  resendVerification: async (email: string): Promise<AuthResponse> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (isCookieAuthMode()) headers['X-Request-Cookie-Auth'] = 'true';
    const response = await apiFetch(`${API_URL}/auth/resend-verification`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email }),
      credentials: isCookieAuthMode() ? 'include' : 'same-origin',
    });
    return response.json();
  },

  requestPasswordReset: async (email: string): Promise<AuthResponse> => {
    const response = await apiFetch(`${API_URL}/auth/request-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return response.json();
  },

  confirmPasswordReset: async (
    access_token: string,
    new_password: string
  ): Promise<AuthResponse> => {
    const response = await apiFetch(`${API_URL}/auth/confirm-password-reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token, new_password }),
    });
    return response.json();
  },
};
